import { useEffect, useState, type FormEvent, type InputHTMLAttributes } from 'react'
import { signIn, signUp } from '../lib/auth-client.ts'

/**
 * Connexion et inscription sur le même écran.
 *
 * Volontairement sans mouvement : c'est un portail, on le traverse rarement
 * et vite. Animer la bascule entre les deux modes coûterait du temps réel à
 * chaque passage pour zéro compréhension gagnée.
 */
export function Connexion() {
  const [mode, setMode] = useState<'connexion' | 'inscription'>('connexion')
  /*
   * Premier démarrage : tant qu'aucun compte n'existe, se connecter est
   * IMPOSSIBLE, et créer le sien est la seule action sensée. On bascule donc
   * l'écran sur l'inscription au lieu d'afficher un formulaire de connexion
   * que personne ne peut remplir.
   *
   * C'est un vrai retour d'usage : la première question posée après le
   * déploiement a été « comment créer un compte ? ». L'affordance était un
   * lien souligné en bas d'écran, libellé « J'ai un code d'invitation » —
   * qui ne dit nulle part qu'il sert à ça.
   */
  const [premier, setPremier] = useState(false)
  const [email, setEmail] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [prenom, setPrenom] = useState('')
  const [codeInvitation, setCodeInvitation] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  useEffect(() => {
    let vivant = true
    void fetch('/api/sante')
      .then((r) => r.json())
      .then((d: { aucunCompte?: boolean }) => {
        if (!vivant || !d.aucunCompte) return
        setPremier(true)
        setMode('inscription')
      })
      .catch(() => {
        /* Hors-ligne : on reste sur la connexion, qui est le cas courant. */
      })
    return () => {
      vivant = false
    }
  }, [])

  async function envoyer(e: FormEvent) {
    e.preventDefault()
    setErreur(null)
    setEnCours(true)

    const resultat =
      mode === 'connexion'
        ? await signIn.email({ email, password: motDePasse })
        : await signUp.email({
            email,
            password: motDePasse,
            name: prenom,
            // Champ supplémentaire lu par le hook serveur : l'inscription
            // est fermée, il n'y a pas de création de compte libre.
            codeInvitation,
          } as Parameters<typeof signUp.email>[0])

    setEnCours(false)
    if (resultat.error) {
      setErreur(traduire(resultat.error.message ?? '', resultat.error.status))
    }
  }

  return (
    <div className="flex min-h-dvh flex-col justify-center bg-canvas px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        <h1 className="font-serif text-display text-ink">Popote</h1>
        <p className="mt-1 text-body text-muted">
          {premier
            ? 'Personne n’a encore de compte. Crée le tien pour démarrer.'
            : 'La cuisine du foyer.'}
        </p>

        <form onSubmit={envoyer} className="mt-8 flex flex-col gap-3">
          {mode === 'inscription' && (
            <Champ
              label="Prénom"
              value={prenom}
              onChange={setPrenom}
              autoComplete="given-name"
              required
            />
          )}

          <Champ
            label="Adresse e-mail"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            inputMode="email"
            required
          />

          <Champ
            label="Mot de passe"
            type="password"
            value={motDePasse}
            onChange={setMotDePasse}
            autoComplete={mode === 'connexion' ? 'current-password' : 'new-password'}
            required
          />

          {mode === 'inscription' && (
            <Champ
              label="Code d'invitation"
              value={codeInvitation}
              onChange={setCodeInvitation}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              aide="Le code partagé du foyer, dans le fichier .env du serveur."
            />
          )}

          {erreur && (
            <p
              role="alert"
              className="rounded-sm bg-epuise-soft px-3 py-2 text-meta text-epuise-ink"
            >
              {erreur}
            </p>
          )}

          <button
            type="submit"
            disabled={enCours}
            className="pressable mt-2 h-[52px] rounded-sm bg-accent text-head text-accent-ink disabled:opacity-60"
          >
            {mode === 'connexion' ? 'Se connecter' : 'Créer mon compte'}
          </button>
        </form>

        {/*
          Au PREMIER démarrage, il n'y a rien à quoi se connecter : proposer
          « J'ai déjà un compte » serait une impasse. On le cache.
        */}
        {!premier && (
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'connexion' ? 'inscription' : 'connexion')
              setErreur(null)
            }}
            /*
              Bouton plein et non lien souligné : créer un compte est une
              destination, pas une note de bas de page. C'est ce qui manquait.
            */
            className="pressable mt-3 h-[52px] w-full rounded-sm bg-surface-sunk text-head text-ink"
          >
            {mode === 'connexion' ? 'Créer un compte' : "J'ai déjà un compte"}
          </button>
        )}
      </div>
    </div>
  )
}

function Champ({
  label,
  value,
  onChange,
  aide,
  ...props
}: {
  label: string
  value: string
  onChange: (v: string) => void
  aide?: string
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-meta text-muted">{label}</span>
      <input
        {...props}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        /* h-[48px] et text-field (17px) : sous 16px, iOS zoome au focus et ne
           dézoome jamais. */
        className="h-[48px] rounded-sm bg-surface-sunk px-3 text-field text-ink outline-none focus-visible:outline-2 focus-visible:outline-accent"
      />
      {aide && <span className="text-meta text-muted">{aide}</span>}
    </label>
  )
}

/** Les messages de better-auth sont en anglais et techniques. */
function traduire(message: string, statut?: number): string {
  if (/invalid email or password/i.test(message)) return 'E-mail ou mot de passe incorrect.'
  if (/already exists|existing/i.test(message)) return 'Un compte existe déjà avec cet e-mail.'
  if (/password/i.test(message) && /short|length/i.test(message))
    return 'Le mot de passe doit faire au moins 8 caractères.'
  if (statut === 403) return "Code d'invitation invalide."
  return message || 'Une erreur est survenue.'
}
